import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CandidateModal = ({ candidate, isOpen, onClose, onScheduleInterview, onSendMessage }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !candidate) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'Plus' },
      'screening': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: 'Clock' },
      'interview': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'Calendar' },
      'offer': { color: 'bg-green-100 text-green-800 border-green-200', icon: 'CheckCircle' },
      'rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: 'X' },
      'hired': { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: 'UserCheck' }
    };

    const config = statusConfig?.[status] || statusConfig?.['new'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={14} className="mr-1.5" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const aiAnalysis = {
    strengths: [
      "Strong technical background in React and Node.js",
      "Excellent problem-solving skills demonstrated in portfolio",
      "Leadership experience managing development teams",
      "Continuous learning mindset with recent certifications"
    ],
    concerns: [
      "Limited experience with cloud platforms",
      "No direct experience in fintech industry",
      "Gap in employment history (6 months in 2023)"
    ],
    recommendations: [
      "Schedule technical interview focusing on system design",
      "Discuss cloud platform experience and learning plans",
      "Explore leadership style and team management approach"
    ]
  };

  const interactionHistory = [
    {
      id: 1,
      type: 'application',
      title: 'Application Submitted',
      description: 'Candidate applied for Senior Frontend Developer position',
      date: '2025-08-20T10:30:00Z',
      icon: 'FileText'
    },
    {
      id: 2,
      type: 'ai-screening',
      title: 'AI Screening Completed',
      description: 'Automated CV analysis completed with 87% compatibility score',
      date: '2025-08-20T11:15:00Z',
      icon: 'Brain'
    },
    {
      id: 3,
      type: 'status-update',
      title: 'Status Updated',
      description: 'Moved to screening stage for manual review',
      date: '2025-08-21T09:00:00Z',
      icon: 'ArrowRight'
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'ai-analysis', label: 'AI Analysis', icon: 'Brain' },
    { id: 'cv-preview', label: 'CV Preview', icon: 'FileText' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-lg shadow-elevation-modal max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
              <Image
                src={candidate?.avatar}
                alt={candidate?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{candidate?.name}</h2>
              <p className="text-muted-foreground">{candidate?.position}</p>
              <div className="flex items-center space-x-3 mt-2">
                {getStatusBadge(candidate?.status)}
                <span className={`text-lg font-bold ${getScoreColor(candidate?.aiScore)}`}>
                  {candidate?.aiScore}% Match
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => onScheduleInterview(candidate)}
              iconName="Calendar"
              iconSize={16}
            >
              Schedule Interview
            </Button>
            <Button
              variant="outline"
              onClick={() => onSendMessage(candidate)}
              iconName="MessageCircle"
              iconSize={16}
            >
              Send Message
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Mail" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{candidate?.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{candidate?.phone || '+1 (555) 123-4567'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{candidate?.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Linkedin" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">linkedin.com/in/{candidate?.name?.toLowerCase()?.replace(' ', '')}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Professional Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Experience:</span>
                      <span className="text-foreground">{candidate?.experience} years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Department:</span>
                      <span className="text-foreground">{candidate?.department}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Applied:</span>
                      <span className="text-foreground">{formatDate(candidate?.applicationDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Salary Expectation:</span>
                      <span className="text-foreground">$120,000 - $140,000</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-3">Key Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {['React', 'Node.js', 'TypeScript', 'AWS', 'MongoDB', 'GraphQL', 'Docker', 'Kubernetes']?.map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai-analysis' && (
            <div className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Brain" size={20} className="text-primary" />
                  <h3 className="font-semibold text-foreground">AI Compatibility Analysis</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Based on CV analysis, job requirements matching, and predictive modeling
                </p>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="TrendingUp" size={16} className="mr-2 text-green-600" />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {aiAnalysis?.strengths?.map((strength, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="AlertTriangle" size={16} className="mr-2 text-yellow-600" />
                  Areas of Concern
                </h4>
                <ul className="space-y-2">
                  {aiAnalysis?.concerns?.map((concern, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{concern}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="Lightbulb" size={16} className="mr-2 text-blue-600" />
                  Recommendations
                </h4>
                <ul className="space-y-2">
                  {aiAnalysis?.recommendations?.map((recommendation, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'cv-preview' && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-2" />
                <h3 className="font-medium text-foreground mb-2">CV Preview</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Original CV document submitted by the candidate
                </p>
                <Button variant="outline" iconName="Download" iconSize={16}>
                  Download CV
                </Button>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-muted-foreground text-sm">
                  CV preview functionality would display the actual document here. 
                  This could include PDF viewer, document thumbnails, or extracted text content.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Interaction History</h3>
              <div className="space-y-4">
                {interactionHistory?.map((item) => (
                  <div key={item?.id} className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{item?.title}</h4>
                      <p className="text-muted-foreground text-sm">{item?.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(item?.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateModal;